import React, { useState, useEffect } from 'react';
import { getCookie } from '../utils';

export default function EnrollStudent({ students, batches, setEnrollments, setActiveTab, selectedBatchId }) {
  
  const currentBatch = batches.find(b => b.id === selectedBatchId);

  // --- STATE ---
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState('');
  // Set default date to today (YYYY-MM-DD format for HTML input)
  const [feesPaidOn, setFeesPaidOn] = useState(new Date().toISOString().split('T')[0]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-calculate End Date when Start Date changes
  const handleStartDateChange = (selectedDate) => {
    
    setStartDate(selectedDate);

    if (selectedDate && currentBatch) {
      const start = new Date(selectedDate);
      start.setDate(start.getDate() + (currentBatch.validDays || 30)); 
      
      const formattedEndDate = start.toISOString().split('T')[0];
      setEndDate(formattedEndDate);
    } else {
      setEndDate('');
    }
  };

  const handleEnroll = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const formData = new FormData(e.target);
    const batchId = formData.get('batchId'); 
    const studentId = formData.get('studentId');
    const batch = batches.find(b => b.id === batchId);

    try {
      const token = getCookie('tsa_token');
      const apiHeaders = { 'Content-Type': 'application/json', 'x-auth-token': token || '' };

      // 1. PREPARE ENROLLMENT PAYLOAD
      const enrollmentPayload = {
        studentId: studentId,
        batchId: batchId,
        EnrolledDate: new Date(formData.get('startDay')).toISOString(),
        ExpiryDate: new Date(formData.get('endDay')).toISOString(),
        TotalHours: batch.hoursCount || 0,
        UsedHours: 0,
        TotalFees: batch.fees || 0,
        PaidFees: Number(formData.get('amountPaid')),
        active: true
      };

      // 2. POST TO ENROLMENTS API
      const enrollRes = await fetch('https://tsaapis.operate.live/api/enrolments', {
        method: 'POST',
        headers: apiHeaders,
        credentials: 'include',
        body: JSON.stringify(enrollmentPayload)
      });

      if (!enrollRes.ok) throw new Error("Failed to create enrollment record");
      const enrollData = await enrollRes.json();
      const newEnrollmentId = enrollData._id || enrollData.id;

      // 3. PREPARE PAYMENT PAYLOAD
      const paymentPayload = {
        EnrolmentId: newEnrollmentId,
        PaidAmount: Number(formData.get('amountPaid')),
        PaidDT: new Date(formData.get('feesPaidOn')).toISOString(),
        Mode: formData.get('modeOfPayment'),
        TransactionRef: formData.get('txRef') || '',
        Status: 'Confirmed',
        Remarks: "Enrolled by Admin"
      };

      // 4. POST TO PAYMENTS API
      const payRes = await fetch('https://tsaapis.operate.live/api/payments', {
        method: 'POST',
        headers: apiHeaders,
        credentials: 'include',
        body: JSON.stringify(paymentPayload)
      });

      if (!payRes.ok) throw new Error("Failed to create payment record");

      // 5. UPDATE LOCAL STATE FOR INSTANT UI REFRESH
      const newLocalEnrollment = {
        id: newEnrollmentId,
        studentId: studentId,
        batchId: batchId,
        startDay: new Date(formData.get('startDay')).toLocaleDateString(),
        endDay: new Date(formData.get('endDay')).toLocaleDateString(),
        usedHours: 0, 
        balanceHours: batch.hoursCount || 0, 
        active: true
      };
      
      setEnrollments(prev => [...prev, newLocalEnrollment]);
      
      alert("Student Enrolled & Payment Recorded Successfully!");
      setActiveTab('batch-details'); 

    } catch (error) {
      console.error("API Error during enrollment:", error);
      alert("Error processing enrollment. Please check your connection and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(()=>handleStartDateChange(startDate),[])

  const readOnlyStyle = { 
    backgroundColor: 'rgba(255,255,255,0.05)', 
    color: 'var(--accent-gold)', 
    fontWeight: 'bold',
    cursor: 'not-allowed'
  };

  return (
    <div className="admin-card">
      <h2>
        <span>Enroll Student</span>
        <button className="btn-small" onClick={() => setActiveTab('batch-details')} disabled={isSubmitting}>
          <i className="fas fa-arrow-left"></i> Cancel
        </button>
      </h2>
      <form onSubmit={handleEnroll}>
        
        {/* SCROLLING CONTENT */}
        <div className="form-grid">
          
          <div className="form-group"><label>Select Student</label>
            <select name="studentId" required defaultValue="" disabled={isSubmitting}>
              <option value="" disabled>Select Student</option>
              {students.map(s => <option key={s.id} value={s.id}>{s.name} ({s.regId})</option>)}
            </select>
          </div>
          
          <div className="form-group"><label>Batch</label>
            <input type="text" value={currentBatch ? currentBatch.title : 'Unknown Batch'} readOnly style={readOnlyStyle} />
            <input type="hidden" name="batchId" value={selectedBatchId || ""} />
          </div>
          
          <div className="form-group">
            <label>Start Day</label>
            <input type="date" name="startDay" required value={startDate} onChange={e=>handleStartDateChange(e.target.value)} disabled={isSubmitting} />
          </div>
          
          <div className="form-group">
            <label>End Day</label>
            <input type="date" name="endDay" required value={endDate} readOnly style={readOnlyStyle} />
          </div>
          
          <div className="form-group">
            <label>Amount Paid (₹)</label>
            <input type="number" name="amountPaid" required value={currentBatch ? currentBatch.fees : ''} readOnly style={readOnlyStyle} />
          </div>
          
          <div className="form-group">
            <label>Fees Paid On</label>
            <input 
              type="date" 
              name="feesPaidOn" 
              required 
              value={feesPaidOn} 
              onChange={(e) => setFeesPaidOn(e.target.value)} 
              disabled={isSubmitting} 
            />
          </div>
          
          <div className="form-group"><label>Mode Of Payment</label>
            <select name="modeOfPayment" required defaultValue="" disabled={isSubmitting}>
              <option value="" disabled>Select Mode</option>
              <option value="Cash">Cash</option>
              <option value="UPI">UPI</option>
              <option value="Card">Card</option>
            </select>
          </div>
          
          <div className="form-group">
            <label>Transaction Ref</label>
            <input type="text" name="txRef" placeholder="e.g. UTR Number (Optional)" disabled={isSubmitting} />
          </div>
        </div>

        {/* PINNED BOTTOM BUTTON */}
        <div className="form-actions">
          <button type="submit" className="submit-btn" disabled={isSubmitting}>
            {isSubmitting ? (
              <><i className="fas fa-spinner fa-spin"></i> Processing...</>
            ) : (
              <><i className="fas fa-check-circle"></i> Confirm Enrollment</>
            )}
          </button>
        </div>

      </form>
    </div>
  );
}